import React, { useEffect, useState } from 'react';
import { ReportData } from '../types';
import { ReportService } from '../services/report.service';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const Report: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user, logout } = useAuth();

  // Determine the name to display based on the user object structure
  const getDisplayName = () => {
    if (!user) return 'User';
    
    // Try both camelCase and snake_case formats
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    } else {
      // Fall back to username if names aren't available
      return user.username || 'User';
    }
  };

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        const data = await ReportService.getReportData();
        setReportData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching report data:', err);
        setError('Failed to fetch report data. Please try again later.');
        setReportData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, []);

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="app">
      <header>
        <h1>Report</h1>
        <div className="user-info">
          <span>Welcome, {getDisplayName()}</span>
          <Link to="/dashboard"><button className="notes-btn">Notes</button></Link>
          <Link to="/exams"><button className="exams-btn">Exams</button></Link>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      <main className="main-content">
        <div className="report-container">
          <h2>System Statistics</h2>
          
          {loading && <div className="loading">Loading report data...</div>}
          
          {error && <div className="error">{error}</div>}
          
          {!loading && !error && (
            <div className="table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Grade</th>
                    <th>Subject</th>
                    <th>Chapters</th>
                    <th>Notes</th>
                    <th>Questions</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData && reportData.length > 0 ? (
                    reportData.map((row, index) => (
                      <tr key={`${row.grade_id}-${row.subject_id}-${index}`}>
                        <td>{row.grade_name}</td>
                        <td>{row.subject_name}</td>
                        <td>{row.chapter_count}</td>
                        <td>{row.notes_count}</td>
                        <td>{row.questions_count}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{textAlign: 'center', padding: '20px'}}>
                        No report data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              
              {reportData.length === 0 && (
                <div className="no-data">No report data available.</div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Report;